import { useFormikContext } from "formik";
import { validateBulkDuplicatePremises } from "src/components/Form/FormComponents/Validations";
import { apiClient } from "src/api/apiService";
import { useBulkDuplicateDialogContext } from "../context";
import { BulkDuplicateFormValues, PremiseWithError } from "../types";

export const useBulkDuplicateActions = () => {
  const { closeDialog } = useBulkDuplicateDialogContext();
  const {
    submitForm,
    values,
    setErrors,
    isSubmitting,
    setSubmitting,
    setFieldValue,
  } = useFormikContext<BulkDuplicateFormValues>();

  const checkDuplicateInDBForAll = async (): Promise<boolean> => {
    try {
      setSubmitting(true);
      const premisesToCheck = values.premises.map((premise) => {
        const updatedSiteDetails = {
          street_number:
            premise.street_number ??
            values.addressObj.site_details.street_number,
          lot_number:
            premise.lot_number ?? values.addressObj.site_details.lot_number,
          block_number:
            premise.block_number ?? values.addressObj.site_details.block_number,
          section_number:
            premise.section_number ??
            values.addressObj.site_details.section_number,
          dp_number:
            premise.dp_number ?? values.addressObj.site_details.dp_number,
          street_name: values.addressObj.site_details.street_name,
          street_suffix: values.addressObj.site_details.street_suffix,
          suburb: values.addressObj.site_details.suburb,
          state: values.addressObj.site_details.state,
          postcode: values.addressObj.site_details.postcode,
          street_type: values.addressObj.site_details.street_type,
          building_name: values.addressObj.site_details.building_name,
        };
        return {
          unit_number: premise.unit_number ?? values.addressObj.unit_number,
          floor_number: premise.floor_number ?? values.addressObj.floor_number,
          unit_type: values.addressObj.unit_type,
          site_details: updatedSiteDetails,
        };
      });

      // Call API for all premises in parallel
      const responses = await Promise.all(
        premisesToCheck.map((premise) =>
          apiClient.post("/draft/premise/existing-record-look-up", premise)
        )
      );

      if (
        responses
          .map((r) => r.data.outcome)
          .some((o) => o === "DUPLICATE_EXISTS")
      ) {
        const result = responses.map((r, i) => {
          const hasDuplicateError = r.data.outcome === "DUPLICATE_EXISTS";
          return {
            ...values.premises[i],
            hasError: hasDuplicateError,
            errors: ["Oops, a request for this address already exists."],
            fieldErrors: {},
          };
        });
        setFieldValue("premises", result);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Failed to check duplicates:", error);
      // If API fails, allow submission to proceed (backend will validate)
      return true;
    } finally {
      setSubmitting(false);
    }
  };

  const checkDuplicateInDB = async (premise: PremiseWithError) => {
    try {
      const updatedSiteDetails = {
        street_number:
          premise.street_number ?? values.addressObj.site_details.street_number,
        lot_number:
          premise.lot_number ?? values.addressObj.site_details.lot_number,
        block_number:
          premise.block_number ?? values.addressObj.site_details.block_number,
        section_number:
          premise.section_number ??
          values.addressObj.site_details.section_number,
        dp_number:
          premise.dp_number ?? values.addressObj.site_details.dp_number,
        street_name: values.addressObj.site_details.street_name,
        street_suffix: values.addressObj.site_details.street_suffix,
        suburb: values.addressObj.site_details.suburb,
        state: values.addressObj.site_details.state,
        postcode: values.addressObj.site_details.postcode,
        street_type: values.addressObj.site_details.street_type,
        building_name: values.addressObj.site_details.building_name,
      };
      const premisesToCheck = {
        unit_number: premise.unit_number ?? values.addressObj.unit_number,
        floor_number: premise.floor_number ?? values.addressObj.floor_number,
        unit_type: values.addressObj.unit_type,
        site_details: updatedSiteDetails,
      };

      // Call API for all premises in parallel
      const response = await apiClient.post(
        "/draft/premise/existing-record-look-up",
        premisesToCheck
      );
      return {
        ...premise,
        hasError: response.data.outcome === "DUPLICATE_EXISTS",
        errors:
          response.data.outcome === "DUPLICATE_EXISTS"
            ? ["Oops, a request for this address already exists."]
            : [],
        fieldErrors: {},
      };
    } catch (error) {
      console.error("Failed to check duplicates:", error);
      return {};
    }
  };

  const handleSubmit = async () => {
    // Check if at least one premise is added
    if (
      values.premises.length === 0 ||
      values.selectedAddressParts.length === 0
    ) {
      if (values.premises.length === 0) {
        setErrors({ premises: "Oops, you need to add at least one premise." });
      }
      if (values.selectedAddressParts.length === 0) {
        setErrors({
          selectedAddressParts:
            "Oops, you must select at least one option. Each premises address must be unique and can't be the same as the original or any existing requests.",
        });
      }
      return;
    }

    const { isValid, errors, fieldErrors } = validateBulkDuplicatePremises(
      values.premises,
      values.selectedAddressParts,
      values.addressObj
    );

    if (!isValid) {
      // Mark premises with errors and set appropriate helper text
      const updatedPremises = values.premises.map((premise, index) => {
        return {
          ...premise,
          hasError: errors[index].length > 0,
          fieldErrors: fieldErrors[index] || {},
          errors: errors[index] || {},
        };
      });
      setFieldValue("premises", updatedPremises);
      return;
    }

    const duplicatesExistsInDB = await checkDuplicateInDBForAll();
    if (duplicatesExistsInDB) {
      return;
    }

    submitForm();
  };

  const buttonText =
    values.premises.length > 0
      ? `Create ${values.premises.length} drafts`
      : "Create drafts";

  return {
    checkDuplicateInDB,
    handleSubmit,
    closeDialog,
    isSubmitting,
    buttonText,
    values,
  };
};
